import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import type { Query, User } from "@/graphql/codegen/graphql"
import { UsersListDocument } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

const LIMIT = 200

interface UseGetProductManagerOptionsReturn {
  productManagerOptions: Option<string>[]
  isFetching: boolean
}

export const useGetProductManagerOptions = (): UseGetProductManagerOptionsReturn => {
  const [offset, setOffset] = useState(0)
  const [productManagers, setProductManagers] = useState<User[]>([])
  const [hasMore, setHasMore] = useState(false)

  const [{ data, fetching }] = useQuery<Pick<Query, "user">>({
    query: UsersListDocument,
    variables: { input: { filter: {}, limit: LIMIT, offset } },
  })

  useEffect(() => {
    if (!data) return

    const paginatedUsers = data.user?.list?.__typename === "UserListSuccess" ? data.user.list.users : []

    setProductManagers((prev) => (offset === 0 ? paginatedUsers : [...prev, ...paginatedUsers]))
    setHasMore(paginatedUsers.length % LIMIT === 0 && paginatedUsers.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (hasMore && !fetching) {
      setOffset((prev) => prev + LIMIT)
    }
  }, [hasMore, fetching])

  const productManagerOptions = useMemo<Option<string>[]>(
    () =>
      productManagers
        .map((user) => ({ value: user.userId, label: user.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [productManagers]
  )

  return { productManagerOptions, isFetching: fetching }
}
